import React, { useContext, useState, useEffect } from 'react';
import { Loading, Button } from 'carbon-components-react';
import { useEmptyCallback, useComponentMount } from 'utilities/hooks.util';
import Utils from 'utilities/helper.util';
import {
  LocalStoreContext,
  LocalDispatchContext
} from '../../localStore/context';
import {
  getOrganisationUpdateLogsStart,
  getOrganisationUpdateLogsReset
} from '../../localStore/actions';

const OrganisationUpdateLogs: React.FC<Props> = ({
  selectedId,
  orgCreatedAt
}) => {
  const localStore = useContext(LocalStoreContext);
  const localDispatch = useContext(LocalDispatchContext);
  const [pageOffset, setPageOffset] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [updateLogs, setUpdateLogs] = useState<Array<any>>([]);
  const { organisationUpdateLogs, getOrgUpdateLogsStatus } = localStore;
  const pageSize: number = 5;

  useComponentMount(() => {
    return () => {
      localDispatch(getOrganisationUpdateLogsReset());
      setUpdateLogs([]);
    };
  });

  const getOrgUpdateLogsData = useEmptyCallback(
    (selectedId: number, queryParams: any) => {
      localDispatch(
        getOrganisationUpdateLogsStart({
          organisationId: selectedId,
          params: queryParams
        })
      );
    }
  );

  const handleLoadMoreClick = () => {
    setPageOffset(pageOffset + pageSize);
  };

  const translateEvent = (
    preUpdate: any,
    postUpdate: any,
    user: any,
    actionType: string,
    entity: string,
    user_id?: number
  ) => {
    const eventTranslation = [];
    // Some actions are performed by internal system. If the user id
    // matched the system user id, we manually set the user name to
    // "System".
    const isActionBySystem = user_id === 0;
    const name = isActionBySystem
      ? 'System'
      : user
      ? `${user?.first_name} ${user?.last_name}`
      : 'Sys Admin';

    if (entity !== 'organisation_restriction_limit') {
      const differences: any = {};
      for (const key in postUpdate) {
        if (key in postUpdate) {
          if (preUpdate[key] !== postUpdate[key]) {
            differences[key] = {
              pre: preUpdate[key],
              post: postUpdate[key]
            };
          }
        }
      }
      for (const key in differences) {
        if (key in differences) {
          let keyValue = key;
          let preValue = differences[key].pre;
          let postValue = differences[key].post;

          if (actionType !== 'create') {
            if (key === 'subscription_start') {
              keyValue = 'subscription start date';
              if (preValue) {
                preValue = Utils.getNewDateWithTimezone(
                  preValue,
                  'Australia/Melbourne'
                ).format('DD/MM/YYYY');
              } else {
                preValue = 'null';
              }
              if (postValue) {
                postValue = Utils.getNewDateWithTimezone(
                  postValue,
                  'Australia/Melbourne'
                ).format('DD/MM/YYYY');
              } else {
                postValue = 'null';
              }
            } else if (key === 'subscription_end') {
              keyValue = 'final contractual end date';
              if (preValue) {
                preValue = Utils.getNewDateWithTimezone(
                  preValue,
                  'Australia/Melbourne'
                ).format('DD/MM/YYYY');
              } else {
                preValue = 'null';
              }
              if (postValue) {
                postValue = Utils.getNewDateWithTimezone(
                  postValue,
                  'Australia/Melbourne'
                ).format('DD/MM/YYYY');
              } else {
                postValue = 'null';
              }
            } else if (key === 'contract_renewal') {
              keyValue = 'subscription end date';
              if (preValue) {
                preValue = Utils.getNewDateWithTimezone(
                  preValue,
                  'Australia/Melbourne'
                ).format('DD/MM/YYYY');
              } else {
                preValue = 'null';
              }
              if (postValue) {
                postValue = Utils.getNewDateWithTimezone(
                  postValue,
                  'Australia/Melbourne'
                ).format('DD/MM/YYYY');
              } else {
                postValue = 'null';
              }
            } else if (key === 'organisation_name') {
              keyValue = 'organisation name';
            } else if (key === 'master_admin') {
              keyValue = 'master administrator';
              preValue = preValue ? preValue : 'null';
            } else if (key === 'organisation_group_id') {
              keyValue = 'organisation group';
              if (preValue && preValue !== 'null') {
                preValue = preValue === 1 ? 'EA' : 'BYDA';
              }
              if (postValue) {
                postValue = postValue === 1 ? 'EA' : 'BYDA';
              }
            } else if (key === 'has_create_enquiry') {
              keyValue = 'BYDA Premium Feature';
              if (preValue === false || preValue === true) {
                preValue = preValue ? 'enable' : 'disable';
              }
              if (postValue === false || postValue === true) {
                postValue = postValue ? 'enable' : 'disable';
              }
            } else if (key === 'email_domains') {
              keyValue = 'Email domains';
              preValue = preValue?.length > 0 ? preValue.join(', ') : '';
              postValue = postValue?.length > 0 ? postValue.join(', ') : '';
            } else if (key === 'use_permalink') {
              keyValue = 'Enquiry permalink';
              if (preValue === false || preValue === true) {
                preValue = preValue ? 'enable' : 'disable';
              }
              if (postValue === false || postValue === true) {
                postValue = postValue ? 'enable' : 'disable';
              }
            } else if (key === 'is_enquiries_shareable') {
              keyValue = 'GIS viewers';
              if (preValue === false || preValue === true) {
                preValue = preValue ? 'enable' : 'disable';
              }
              if (postValue === false || postValue === true) {
                postValue = postValue ? 'enable' : 'disable';
              }
            } else if (key === 'is_only_match_role') {
              keyValue = 'SSO group role matching';
              if (preValue === false || preValue === true) {
                preValue = preValue ? 'enable' : 'disable';
              }
              if (postValue === false || postValue === true) {
                postValue = postValue ? 'enable' : 'disable';
              }
            } else if (key === 'is_allow_auto_join') {
              keyValue = 'SSO allow users from the organisation to auto join';
              if (preValue === false || preValue === true) {
                preValue = preValue ? 'enable' : 'disable';
              }
              if (postValue === false || postValue === true) {
                postValue = postValue ? 'enable' : 'disable';
              }
            }

            const translation = (
              <div className="event-content-wrapper">
                {key === 'is_inactive' ? (
                  <>
                    <span className="emphasis">{name}</span>{' '}
                    {postValue ? 'suspended' : 'unsuspended'} the organisation.
                  </>
                ) : (
                  <>
                    <span className="emphasis">{name}</span> updated {keyValue}{' '}
                    from {preValue} to{' '}
                    <span className="emphasis">{postValue}.</span>
                  </>
                )}
              </div>
            );
            eventTranslation.push(translation);
          }
        }
      }
      if (actionType === 'create') {
        const translation = (
          <div className="event-content-wrapper">
            <span className="emphasis">{name}</span> created this organisation.
          </div>
        );
        eventTranslation.push(translation);
      }
    } else {
      if (actionType === 'create') {
        switch (postUpdate.type) {
          case 'ADD_USER':
            eventTranslation.push(
              <div className="event-content-wrapper">
                <span className="emphasis">{name}</span> added{' '}
                <span className="emphasis">{postUpdate.amount}</span> as user
                limit.
              </div>
            );
            break;
          case 'ADD_TEAM':
            eventTranslation.push(
              <div className="event-content-wrapper">
                <span className="emphasis">{name}</span> added{' '}
                <span className="emphasis">{postUpdate.amount}</span> as team
                limit.
              </div>
            );
            break;
          case 'CREATE_ENQUIRY':
            eventTranslation.push(
              <div className="event-content-wrapper">
                <span className="emphasis">{name}</span> added transaction limit
                with amount{' '}
                <span className="emphasis">{postUpdate.amount}</span> and
                reference{' '}
                <span className="emphasis">{postUpdate.reference}</span>.
              </div>
            );
            break;
        }
      } else if (actionType === 'remove') {
        switch (preUpdate.type) {
          case 'ADD_USER':
            eventTranslation.push(
              <div className="event-content-wrapper">
                <span className="emphasis">{name}</span> removed user limit.
              </div>
            );
            break;
          case 'ADD_TEAM':
            eventTranslation.push(
              <div className="event-content-wrapper">
                <span className="emphasis">{name}</span> removed team limit.
              </div>
            );
            break;
          case 'CREATE_ENQUIRY':
            eventTranslation.push(
              <div className="event-content-wrapper">
                <span className="emphasis">{name}</span> removed transaction
                limit with reference{' '}
                <span className="emphasis">{preUpdate.reference}</span>.
              </div>
            );
            break;
        }
      } else {
        switch (postUpdate.type) {
          case 'ADD_USER':
            eventTranslation.push(
              <div className="event-content-wrapper">
                <span className="emphasis">{name}</span> updated user limit from{' '}
                {preUpdate.amount} to{' '}
                <span className="emphasis">{postUpdate.amount}</span>.
              </div>
            );
            break;
          case 'ADD_TEAM':
            eventTranslation.push(
              <div className="event-content-wrapper">
                <span className="emphasis">{name}</span> updated team limit from{' '}
                {preUpdate.amount} to{' '}
                <span className="emphasis">{postUpdate.amount}</span>.
              </div>
            );
            break;
        }
      }
    }
    return eventTranslation;
  };

  useEffect(() => {
    const queryParams = {
      limit: pageSize,
      order: '-created_at',
      offset: pageOffset
    };
    getOrgUpdateLogsData(selectedId, queryParams);
  }, [pageSize, pageOffset, getOrgUpdateLogsData, selectedId]);

  useEffect(() => {
    if (organisationUpdateLogs && getOrgUpdateLogsStatus === 'success') {
      setTotalCount(organisationUpdateLogs.count);
      let hasCreateData = false;
      const logsData = organisationUpdateLogs.data.map((log: any) => {
        if (log.action_type === 'create' && log.entity === 'organisations') {
          hasCreateData = true;
        }
        const preUpdate = log.pre_update;
        const postUpdate = log.post_update;

        const data = {
          time: Utils.toLocalDateTimeFormat(log.created_at),
          event: translateEvent(
            preUpdate,
            postUpdate,
            log.user,
            log.action_type,
            log.entity,
            log.user_id
          )
        };
        return data;
      });

      let combineData = [...updateLogs, ...logsData];

      if (
        combineData.length >= organisationUpdateLogs.count &&
        !hasCreateData
      ) {
        const orgCreationData = [
          {
            time: Utils.toLocalDateTimeFormat(orgCreatedAt),
            event: (
              <div className="event-content-wrapper">
                <span className="emphasis">Sys Admin</span> created this
                organisation.
              </div>
            )
          }
        ];
        combineData = [...combineData, ...orgCreationData];
      }
      setUpdateLogs(combineData);
    }
    // eslint-disable-next-line
  }, [getOrgUpdateLogsStatus, organisationUpdateLogs]);

  return (
    <div className="sys-admin-org-update-logs">
      {updateLogs.length > 0 && (
        <>
          {updateLogs.map((log: any, index: number) => {
            return (
              <div className="sys-admin-org-update-logs-content" key={index}>
                <div className="sys-admin-org-update-logs-content-indicator">
                  <div className="sys-admin-org-update-logs-content-indicator-dot"></div>
                  <div
                    className={`sys-admin-org-update-logs-content-indicator-line${
                      index === updateLogs.length - 1 ? '-last-child' : ''
                    }`}
                  ></div>
                </div>
                <div className="sys-admin-org-update-logs-content-body">
                  <div className="sys-admin-org-update-logs-content-body-time">
                    {log.time}
                  </div>
                  <div className="sys-admin-org-update-logs-content-body-event">
                    {log.event}
                  </div>
                </div>
              </div>
            );
          })}
          <div className="sys-admin-org-update-logs-action-button">
            <Button
              kind="tertiary"
              onClick={handleLoadMoreClick}
              disabled={updateLogs.length >= totalCount}
            >
              Load more...
            </Button>
          </div>
        </>
      )}
      {getOrgUpdateLogsStatus === 'fetching' && <Loading withOverlay={true} />}
    </div>
  );
};

type Props = {
  selectedId: number;
  selectedTabIndex: number;
  orgCreatedAt: string;
};

export default React.memo(OrganisationUpdateLogs);
